import React, { useState } from 'react';

import BackButton from './BackButton';
import Button from '../../../common2/Button';
import ExitClaimsModal from '../ExitClaimsModal';
import SidePanelContainer from '../../../common2/side-panel/SidePanelContainer';
import SidePanelHeader from '../../../common2/side-panel/SidePanelHeader';
import SidePanelContent from '../../../common2/side-panel/SidePanelContent';
import SidePanelFooter from '../../../common2/side-panel/SidePanelFooter';
import Stretch from '../../../common/layout/Stretch';
import { generateNameFromText } from '../../../../slug';
import ButtonsGroup from '../../../common/layout/ButtonsGroup';
import { TransitionOptions } from '../../../claims-center/submit-claims/useSubmitClaimWorkflow';

export type OnNextCallback = ((options?: TransitionOptions) => void) | null;
export type OnSaveDraftCallback = (() => void) | null;

export type ClaimStepContainerProps = {
  subHeading?: string;
  confirmExit?: boolean;
  onClose: () => void;
  onBack?: (() => void) | null;
  onNext?: OnNextCallback;
  onSaveDraft?: OnSaveDraftCallback;
  isSubmitButton?: boolean;
  isDisabled?: boolean;
  isSubmitting?: boolean;
  isClaimDraftSaving?: boolean;
  children?: React.ReactNode;
  headerLeftElement?: React.ReactNode;
  name?: string;
  isExtractingInvoice?: boolean;
  isFailedToExtractInvoice?: boolean;
  extractInvoiceData?: (fileId: number) => void;
  isLoadingIsOrderItemsRequired?: boolean;
};

function ClaimStepContainer(props: ClaimStepContainerProps) {
  const {
    subHeading,
    onBack,
    onNext,
    onClose,
    onSaveDraft,
    headerLeftElement,
    isSubmitButton = false,
    isDisabled,
    confirmExit,
    children,
    isSubmitting,
    isClaimDraftSaving,
    name,
  } = props;

  const nextLabel = isSubmitButton ? 'Submit claim' : 'Next';
  const nextButtonName = generateNameFromText(`${name} ${nextLabel}`);
  const backButtonName = generateNameFromText(`${name} back`);

  const [isExitModalShown, setExitModalShown] = useState(false);

  const onClickCloseButton = () => {
    if (confirmExit) {
      setExitModalShown(true);
    } else {
      onClose();
    }
  };

  const isButtonDisabled = isDisabled || isSubmitting || isClaimDraftSaving;
  const isSaveDraftButtonEnabled = !!onSaveDraft;

  return (
    <SidePanelContainer>
      <SidePanelHeader
        leftElement={headerLeftElement}
        onClose={onClickCloseButton}
        heading="Submit a claim"
        subHeading={subHeading}
      />
      <SidePanelContent>{children}</SidePanelContent>
      <SidePanelFooter>
        {onBack && <BackButton onClick={onBack} name={backButtonName} disabled={isButtonDisabled} />}
        <Stretch />
        <ButtonsGroup>
          {isSaveDraftButtonEnabled && (
            <Button
              name="save-draft"
              intent="base"
              onClick={onSaveDraft}
              disabled={isButtonDisabled}
              isLoading={isClaimDraftSaving}
            >
              Save draft
            </Button>
          )}
          {onNext && (
            <Button onClick={onNext} disabled={isButtonDisabled} isLoading={isSubmitting} name={nextButtonName}>
              {nextLabel}
            </Button>
          )}
        </ButtonsGroup>
      </SidePanelFooter>
      <ExitClaimsModal show={isExitModalShown} onExit={onClose} onCancel={() => setExitModalShown(false)} />
    </SidePanelContainer>
  );
}

export default React.memo(ClaimStepContainer);
